<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import wootConstants from 'dashboard/constants/globals';
import { findSnoozeTime } from 'dashboard/helper/snoozeHelpers';

const props = defineProps({
  conversation: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();

const agents = useMapGetter('agents/getAgents');
const inboxAgents = useMapGetter('inboxAssignableAgents/getAssignableAgents');

const assignee = computed(() => props.conversation.meta?.assignee || null);
const priority = computed(() => props.conversation.priority || null);
const status = computed(() => props.conversation.status || 'open');

// Use inbox-scoped agents if available, fall back to all account agents.
const assignableAgents = computed(() => {
  const scoped = inboxAgents.value?.[props.conversation.inbox_id];
  return scoped?.length ? scoped : agents.value || [];
});

// ── Assignee popover ──
const showAssigneeMenu = ref(false);
const assigneeFilter = ref('');
const filteredAgents = computed(() => {
  const q = assigneeFilter.value.trim().toLowerCase();
  const list = assignableAgents.value;
  if (!q) return list;
  return list.filter(
    a => a.name?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q)
  );
});
const setAssignee = async agent => {
  showAssigneeMenu.value = false;
  try {
    await ConversationApi.assignAgent({
      conversationId: props.conversation.id,
      agentId: agent?.id || 0,
    });
    useAlert(
      agent
        ? t('HOT_OPTIONS.ASSIGNED_TO', { name: agent.name })
        : t('HOT_OPTIONS.UNASSIGNED')
    );
  } catch (e) {
    useAlert(t('HOT_OPTIONS.ASSIGN_FAILED'));
  }
};

// ── Priority cycler ──
// Bloom: keep a dotColor lookup but use Tailwind utility classes
// (bg-bloom-*) instead of inline CSS variables.
const PRIORITY_ORDER = [null, 'low', 'medium', 'high', 'urgent'];
const PRIORITY_META = {
  null: { labelKey: 'HOT_OPTIONS.PRIORITY.NONE', dot: 'bg-n-slate-7' },
  low: { labelKey: 'HOT_OPTIONS.PRIORITY.LOW', dot: 'bg-n-slate-9' },
  medium: {
    labelKey: 'HOT_OPTIONS.PRIORITY.MEDIUM',
    dot: 'bg-bloom-info-default',
  },
  high: {
    labelKey: 'HOT_OPTIONS.PRIORITY.HIGH',
    dot: 'bg-bloom-warning-default',
  },
  urgent: {
    labelKey: 'HOT_OPTIONS.PRIORITY.URGENT',
    dot: 'bg-bloom-error-default',
  },
};
const priorityMeta = computed(() => PRIORITY_META[priority.value ?? 'null']);
const priorityLabel = computed(() => t(priorityMeta.value.labelKey));
const cyclePriority = async () => {
  const idx = PRIORITY_ORDER.indexOf(priority.value);
  const next = PRIORITY_ORDER[(idx + 1) % PRIORITY_ORDER.length];
  try {
    // Dispatch goes through the store, which updates the conversation
    // record in place reactively. Avoids mutating the prop directly
    // (Vue forbids that; eslint flags it).
    await store.dispatch('assignPriority', {
      conversationId: props.conversation.id,
      priority: next,
    });
  } catch (e) {
    useAlert(t('HOT_OPTIONS.PRIORITY_FAILED'));
  }
};

// ── Status pill ──
const STATUS_META = {
  open: {
    labelKey: 'HOT_OPTIONS.STATUS.OPEN',
    classes: 'bg-bloom-success-subtle text-bloom-text-success',
  },
  pending: {
    labelKey: 'HOT_OPTIONS.STATUS.PENDING',
    classes: 'bg-bloom-warning-subtle text-bloom-text-warning',
  },
  snoozed: {
    labelKey: 'HOT_OPTIONS.STATUS.SNOOZED',
    classes: 'bg-bloom-warning-subtle text-bloom-text-warning',
  },
  resolved: {
    labelKey: 'HOT_OPTIONS.STATUS.RESOLVED',
    classes: 'bg-bloom-grey-soft text-bloom-text-tertiary',
  },
};
const statusMeta = computed(
  () => STATUS_META[status.value] || STATUS_META.open
);
const statusLabel = computed(() => t(statusMeta.value.labelKey));

// ── Snooze ──
const showSnoozeMenu = ref(false);
const snoozePresets = [
  {
    labelKey: 'HOT_OPTIONS.SNOOZE.IN_ONE_HOUR',
    key: wootConstants.SNOOZE_OPTIONS.AN_HOUR_FROM_NOW,
  },
  {
    labelKey: 'HOT_OPTIONS.SNOOZE.TOMORROW',
    key: wootConstants.SNOOZE_OPTIONS.UNTIL_TOMORROW,
  },
  {
    labelKey: 'HOT_OPTIONS.SNOOZE.NEXT_WEEK',
    key: wootConstants.SNOOZE_OPTIONS.UNTIL_NEXT_WEEK,
  },
  {
    labelKey: 'HOT_OPTIONS.SNOOZE.NEXT_MONTH',
    key: wootConstants.SNOOZE_OPTIONS.UNTIL_NEXT_MONTH,
  },
];
const snoozeBy = async preset => {
  showSnoozeMenu.value = false;
  const snoozedUntil = findSnoozeTime(preset.key);
  if (!snoozedUntil) return;
  try {
    await store.dispatch('toggleStatus', {
      conversationId: props.conversation.id,
      status: wootConstants.STATUS_TYPE.SNOOZED,
      snoozedUntil,
    });
    useAlert(t('HOT_OPTIONS.SNOOZED_FOR', { duration: t(preset.labelKey) }));
  } catch (e) {
    useAlert(t('HOT_OPTIONS.SNOOZE_FAILED'));
  }
};
const openCustomSnooze = () => {
  showSnoozeMenu.value = false;
  // Reuses Chatwoot's existing command-palette flow for the custom datetime
  // picker so behavior stays consistent with the rest of the app.
  const ninja = document.querySelector('ninja-keys');
  ninja?.open({ parent: 'snooze_conversation' });
};

// Click outside to close popovers
const rootRef = ref(null);
const handleClickOutside = e => {
  if (rootRef.value && !rootRef.value.contains(e.target)) {
    showAssigneeMenu.value = false;
    showSnoozeMenu.value = false;
  }
};
onMounted(() => document.addEventListener('mousedown', handleClickOutside));
onUnmounted(() =>
  document.removeEventListener('mousedown', handleClickOutside)
);

// Pre-fetch agents if not loaded yet so the menu opens instantly.
onMounted(() => {
  if (!agents.value?.length) store.dispatch('agents/get');
});
</script>

<template>
  <div
    ref="rootRef"
    class="hot-options-bar flex items-center gap-1.5 text-xs select-none"
  >
    <!-- ASSIGNEE -->
    <div class="relative">
      <button
        v-tooltip="
          assignee
            ? $t('HOT_OPTIONS.TOOLTIP_REASSIGN', { name: assignee.name })
            : $t('HOT_OPTIONS.TOOLTIP_ASSIGN')
        "
        type="button"
        :aria-haspopup="true"
        :aria-expanded="showAssigneeMenu"
        class="flex items-center gap-1.5 px-1.5 py-1 rounded-md transition-colors hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bloom-purple/40"
        @click="showAssigneeMenu = !showAssigneeMenu"
      >
        <template v-if="assignee">
          <Avatar
            :name="assignee.name"
            :src="assignee.thumbnail"
            :size="20"
            rounded-full
          />
          <span
            class="truncate max-w-[110px] font-medium text-bloom-text-secondary"
          >
            {{ assignee.name }}
          </span>
        </template>
        <template v-else>
          <div
            class="w-5 h-5 rounded-full flex items-center justify-center text-xxs bg-bloom-grey-soft text-bloom-text-tertiary"
          >
            <Icon icon="i-lucide-user" class="size-3" aria-hidden="true" />
          </div>
          <span class="text-bloom-text-tertiary">
            {{ $t('HOT_OPTIONS.UNASSIGNED') }}
          </span>
        </template>
      </button>
      <Transition
        enter-active-class="transition duration-150 ease-out"
        enter-from-class="opacity-0 -translate-y-1"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showAssigneeMenu"
          class="absolute z-40 mt-1 left-0 min-w-[240px] rounded-lg shadow-lg border overflow-hidden bg-bloom-bg-primary border-bloom-border-default"
        >
          <div class="px-2 py-1.5 border-b border-bloom-border-subtle">
            <input
              v-model="assigneeFilter"
              type="text"
              :placeholder="$t('HOT_OPTIONS.FILTER_PLACEHOLDER')"
              class="w-full text-xs px-2 py-1 rounded outline-none focus:ring-2 focus:ring-bloom-purple/30 bg-bloom-grey-soft text-bloom-text-primary"
            />
          </div>
          <div class="max-h-64 overflow-y-auto py-1">
            <button
              type="button"
              class="flex items-center gap-2 w-full px-3 py-1.5 hover:bg-n-alpha-2 text-left"
              @click="setAssignee(null)"
            >
              <span
                class="w-5 h-5 rounded-full flex items-center justify-center text-xxs bg-bloom-grey-soft text-bloom-text-tertiary"
              >
                <Icon
                  icon="i-lucide-user-x"
                  class="size-3"
                  aria-hidden="true"
                />
              </span>
              <span class="text-xs text-bloom-text-tertiary">
                {{ $t('HOT_OPTIONS.UNASSIGNED') }}
              </span>
            </button>
            <button
              v-for="agent in filteredAgents"
              :key="agent.id"
              type="button"
              class="flex items-center gap-2 w-full px-3 py-1.5 hover:bg-n-alpha-2 text-left"
              :class="{ 'bg-n-alpha-1': assignee?.id === agent.id }"
              @click="setAssignee(agent)"
            >
              <Avatar
                :name="agent.name"
                :src="agent.thumbnail"
                :size="20"
                rounded-full
              />
              <span class="text-xs font-medium text-bloom-text-primary">
                {{ agent.name }}
              </span>
              <span
                v-if="agent.availability_status === 'online'"
                class="ml-auto w-1.5 h-1.5 rounded-full bg-bloom-success-default"
              />
            </button>
            <div
              v-if="!filteredAgents.length"
              class="px-3 py-2 text-xs text-bloom-text-tertiary"
            >
              {{ $t('HOT_OPTIONS.FILTER_EMPTY', { query: assigneeFilter }) }}
            </div>
          </div>
        </div>
      </Transition>
    </div>

    <!-- PRIORITY -->
    <button
      v-tooltip="$t('HOT_OPTIONS.TOOLTIP_PRIORITY', { label: priorityLabel })"
      type="button"
      class="flex items-center gap-1.5 px-1.5 py-1 rounded-md transition-colors hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bloom-purple/40"
      @click="cyclePriority"
    >
      <span
        class="w-2 h-2 rounded-full"
        :class="priorityMeta.dot"
        aria-hidden="true"
      />
      <span class="text-bloom-text-tertiary">{{ priorityLabel }}</span>
    </button>

    <!-- SNOOZE quick-action -->
    <div class="relative">
      <button
        v-tooltip="$t('HOT_OPTIONS.TOOLTIP_SNOOZE')"
        type="button"
        :aria-haspopup="true"
        :aria-expanded="showSnoozeMenu"
        class="flex items-center gap-1 px-1.5 py-1 rounded-md transition-colors hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bloom-purple/40"
        @click="showSnoozeMenu = !showSnoozeMenu"
      >
        <Icon icon="i-lucide-alarm-clock" class="size-3" aria-hidden="true" />
        <span class="text-bloom-text-tertiary">
          {{ $t('HOT_OPTIONS.SNOOZE.LABEL') }}
        </span>
      </button>
      <Transition
        enter-active-class="transition duration-150"
        enter-from-class="opacity-0 -translate-y-1"
        enter-to-class="opacity-100"
      >
        <div
          v-if="showSnoozeMenu"
          class="absolute z-40 mt-1 right-0 min-w-[200px] rounded-lg shadow-lg border py-1 bg-bloom-bg-primary border-bloom-border-default"
        >
          <button
            v-for="p in snoozePresets"
            :key="p.key"
            type="button"
            class="w-full text-left text-xs px-3 py-1.5 hover:bg-n-alpha-2 transition-colors text-bloom-text-primary"
            @click="snoozeBy(p)"
          >
            {{ $t(p.labelKey) }}
          </button>
          <div class="border-t my-1 border-bloom-border-subtle" />
          <button
            type="button"
            class="w-full text-left text-xs px-3 py-1.5 hover:bg-n-alpha-2 transition-colors flex items-center gap-1.5 text-bloom-text-secondary"
            @click="openCustomSnooze"
          >
            <Icon icon="i-lucide-calendar" class="size-3" aria-hidden="true" />
            <span>{{ $t('HOT_OPTIONS.SNOOZE.CUSTOM') }}</span>
          </button>
        </div>
      </Transition>
    </div>

    <!-- STATUS PILL -->
    <span
      v-tooltip="$t('HOT_OPTIONS.TOOLTIP_STATUS', { label: statusLabel })"
      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
      :class="statusMeta.classes"
    >
      {{ statusLabel }}
    </span>
  </div>
</template>
